import React, { ReactNode, cloneElement, useEffect, useState, useRef } from 'react';
import {
  useFloating,
  arrow as arrowMiddleware,
  shift,
  autoUpdate,
  offset,
  useInteractions,
  useHover,
  useRole,
  useDismiss,
  useClick,
  FloatingPortal,
  hide as hideMiddleware,
  OpenChangeReason,
} from '@floating-ui/react';
import classNames from 'classnames';
import { Placement, placementToCSSProperty } from '../../styles';
import { TooltipBaseProps, TooltipBase } from './TooltipBase';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { Stack } from '../stack';
import { CloseButton } from '../button';
import { semantic_colors } from '../../foundation';
import { TooltipSize } from './types';

export type OpenerTriggerEvent = 'hover' | 'click' | 'none';
/**
 * 툴팁이 `opened` prop에 의해 변경된 경우에는 `reason`이 `null`이 됩니다.
 */
export type TooltipOpenChangeReason = 'click' | 'closeClick' | 'overlayClick' | 'hover' | null;
type TooltipContentWithCloseAction = (action: { close(): void }) => ReactNode;

const closeButtonMarginTop: Record<TooltipSize, number> = {
  small: 1,
  medium: 0,
  large: 0,
};

const closeButtonSize: Record<TooltipSize, number> = {
  small: 12,
  medium: 16,
  large: 18,
};

export interface TooltipProps extends TooltipBaseProps {
  /** reference element를 구할 수 있는 컴포넌트여야 합니다. */
  children: React.ReactNode;
  className?: string;
  content?: ReactNode | TooltipContentWithCloseAction;
  /**
   * outside 영역 클릭시 닫힘 사용 여부
   * @default true
   */
  canClickOutside?: boolean;
  /** @default 'hover' */
  openerTriggerEvent?: OpenerTriggerEvent;
  /** @default 'top' */
  placement?: Exclude<Placement, 'bottom-end'>;
  /** @default getZIndex('tooltip') */
  zIndex?: number;
  /**
   * main app node 바깥영역으로 생성할지 여부
   * @default false
   */
  noUsePortal?: boolean;
  /**
   * Hover 후 닫히는 지연 시간
   * @default 250
   */
  close_delay?: number;
  opened?: boolean;
  onClose?: (reason: TooltipOpenChangeReason) => void;
  onOpen?: (reason: TooltipOpenChangeReason) => void;
}
export const Tooltip = ({
  placement = 'top',
  children,
  className,
  content,
  canClickOutside = true,
  openerTriggerEvent = 'hover',
  opened: openProp,
  size = 'medium',
  kind = 'accent',
  zIndex = getZIndex('tooltip'),
  noUsePortal = false,
  close_delay = 250,
  onClose,
  onOpen,
}: TooltipProps) => {
  const [floating_state, setFloatingState] = useState<{
    open: boolean;
    reason: TooltipOpenChangeReason;
  }>({ open: openProp ?? false, reason: null });
  const arrowElement = useRef<HTMLDivElement | null>(null);
  const arrowElementStaticSide = placementToCSSProperty(placement);

  const handleClose = () => {
    onClose ? onClose('closeClick') : setFloatingState({ open: false, reason: 'closeClick' });
  };

  const {
    refs,
    floatingStyles,
    context,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {}, hide },
  } = useFloating({
    placement,
    open: floating_state.open,
    onOpenChange: (open, _event, reason: Extract<OpenChangeReason, 'outside-press' | 'click' | 'hover'>) => {
      setFloatingState({ open, reason: reason === 'outside-press' ? 'overlayClick' : reason });
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(9),
      shift({ padding: 3 }),
      arrowMiddleware({ element: arrowElement, padding: 4 }),
      hideMiddleware(),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: openerTriggerEvent === 'hover' && openProp !== false,
      delay: {
        open: 0,
        close: close_delay,
      },
    }),
    useClick(context, { enabled: openerTriggerEvent === 'click' }),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context, { outsidePress: canClickOutside }),
  ]);

  useEffect(() => {
    if (openProp !== undefined && openerTriggerEvent === 'none') {
      setFloatingState({ open: openProp, reason: null });
    }
  }, [openProp, openerTriggerEvent]);

  useEffect(() => {
    floating_state.open ? onOpen?.(floating_state.reason) : onClose?.(floating_state.reason);
  }, [floating_state]);

  const renderTooltipBase = () => {
    return (
      <TooltipBase
        {...getFloatingProps({
          className: classNames('pds-tooltip', className),
          ref: refs.setFloating,
          style: {
            ...floatingStyles,
            zIndex,
            display: hide?.referenceHidden ? 'none' : 'block',
          },
        })}
        size={size}
        kind={kind}
      >
        <Stack direction='row' gap={2}>
          {typeof content === 'function' ? content({ close: handleClose }) : content}
          {/* Neutral 속성일 경우는 closeButton 노출 안함 */}
          {kind !== 'neutral' && openerTriggerEvent !== 'hover' && (
            <CloseButton
              className='pds-tooltip-close-button'
              size={closeButtonSize[size]}
              color={semantic_colors.content.primary_inverse}
              style={{ alignItems: 'flex-start', marginTop: closeButtonMarginTop[size] }}
              onClick={handleClose}
            />
          )}
        </Stack>
        <div
          className={classNames('pds-tooltip__arrow', placement)}
          ref={arrowElement}
          css={{
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            [arrowElementStaticSide]: '-3px',
          }}
        />
      </TooltipBase>
    );
  };

  return (
    <>
      {cloneElement(
        children as React.ReactElement,
        getReferenceProps({ ref: refs.setReference, ...(children as React.ReactElement).props }),
      )}
      {floating_state.open &&
        (noUsePortal ? (
          renderTooltipBase()
        ) : (
          <FloatingPortal id={FLOATING_ROOT_ID}>{renderTooltipBase()}</FloatingPortal>
        ))}
    </>
  );
};
